import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AgentStateService } from '@core/services/agent-state.service';
import { ChatStateService } from '@core/services/chat-state.service';
import { CollapsibleMenuStateService } from '@core/services/collapsible-menu-state.service';
import { NavbarViewManagerService } from '@core/services/navbar-view-manager.service';
import {
  NavigationShellStateService,
  NavigationShellState,
} from '@core/services/navigation-shell-state.service';
import { ProfileService } from '@core/services/profile.service';
import { AuthenticationService } from '@core/services/auth.service';
import { ScreenSize } from '@core/services/responsive.service';
import { SidebarService } from '@core/services/side-bar.service';
import {
  AccessNextStep,
  TrialInfo,
  AccessStateService,
} from '@core/services/access-state.service';
import {
  SidebarProfileContent,
  SidebarProfileStateService,
} from '@core/services/sidebar-profile-state.service';
import { DashboardSidebarAction } from '@shared/sidebar-navigation/sidebar-navigation.config';
import { DashboardBackofficeEntryService } from './dashboard-backoffice-entry.service';
import { isSuperadminUser } from '@core/guards/superadmin.guard';

type ActiveMenuSection =
  | 'home'
  | 'training'
  | 'agents'
  | 'connections'
  | 'knowledge'
  | 'alerts'
  | 'clients'
  | 'profile'
  | 'company'
  | 'users'
  | 'payments'
  | 'help-center'
  | null;

@Injectable()
export class DashboardShellFacade {
  isOpen = true;
  isMobile = false;
  isTablet = false;
  showHome = true;
  showSidebarMenu = false;
  showTopNavbar = true;
  sidebarContent: SidebarProfileContent = 'menu';
  isMenuExpanded = false;
  isProfileSidebarExpanded = true;
  activeMenuSection: ActiveMenuSection = 'home';
  accessNextStep: AccessNextStep = 'ready';
  showAccessAlert = false;
  accessAlertTitle = '';
  accessAlertMessage = '';
  accessAlertType: 'info' | 'success' | 'warning' | 'alert' = 'warning';
  accessAlertToken = 0;
  isLoggingOut = false;
  trialInfo: TrialInfo | null = null;
  hasActiveSubscription = false;
  hasActiveSubscriptionFromSession = false;
  /** Rol admin — controla la visibilidad del ítem "Clientes" del sidebar. */
  isSuperadmin = false;
  /** 'alerts' en blocked_modules: el ítem del sidebar lleva la pastilla de candado. */
  isAlertsLocked = false;

  private readonly ACTIVE_MENU_KEY = 'active_menu_section';
  private subscriptions: Subscription[] = [];
  private menuExpandedBeforeSettings: boolean | null = null;
  private profileSidebarExpandedBeforeSettings: boolean | null = null;
  private accessAlertTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private agentStateService: AgentStateService,
    private chatStateService: ChatStateService,
    private collapsibleMenuStateService: CollapsibleMenuStateService,
    private navigationShellStateService: NavigationShellStateService,
    private navbarViewManagerService: NavbarViewManagerService,
    private profileService: ProfileService,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private sidebarProfileStateService: SidebarProfileStateService,
    private sidebarService: SidebarService,
    private dashboardBackofficeEntryService: DashboardBackofficeEntryService,
    private accessStateService: AccessStateService,
  ) {}

  init(): void {
    if (
      this.dashboardBackofficeEntryService.handleInitialEntry(
        this.route,
        this.router,
      )
    ) {
      return;
    }

    this.loadActiveMenuSection();
    this.updateActiveMenuFromRoute();
    this.subscriptions.push(
      this.accessStateService.accessState$.subscribe((state) => {
        this.accessNextStep = state?.next_step ?? 'ready';
        this.hasActiveSubscription = !!state?.subscription?.active;
        this.isAlertsLocked = !this.accessStateService.hasModule('alerts', state);
        this.enforceAccessRoute();
      }),
    );
    this.subscriptions.push(this.accessStateService.refresh().subscribe());

    this.subscriptions.push(
      this.authService.logoutInProgress$.subscribe((isLoggingOut) => {
        this.isLoggingOut = isLoggingOut;
      }),
    );
    this.subscriptions.push(
      this.authService.trial$.subscribe((trial) => {
        this.trialInfo = trial;
      }),
    );
    this.subscriptions.push(
      this.authService.currentUser$.subscribe((user) => {
        const subscription = user?.subscription;
        const status = String(subscription?.status || '').trim().toLowerCase();
        this.hasActiveSubscriptionFromSession =
          !!subscription?.active ||
          ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(status);
        this.isSuperadmin = isSuperadminUser(user);
      }),
    );

    this.subscriptions.push(
      this.navigationShellStateService.state$.subscribe((state) => {
        this.applyNavigationShellState(state);
      }),
    );

    this.subscriptions.push(
      this.sidebarProfileStateService.currentContent$.subscribe((content) => {
        this.sidebarContent = content;
      }),
    );

    this.subscriptions.push(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateActiveMenuFromRoute();
        }),
    );
  }

  destroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.accessAlertTimer) {
      clearTimeout(this.accessAlertTimer);
      this.accessAlertTimer = null;
    }
  }

  get isBackofficeMode(): boolean {
    const url = this.router.url;

    return (
      url.includes('/home-backoffice') ||
      url.includes('/payments-invoices-backoffice') ||
      (url.includes('/profile') && url.includes('mode=backoffice')) ||
      (url.includes('/settings') && url.includes('mode=backoffice'))
    );
  }

  isPersonalProfileRoute(): boolean {
    return this.router.url === '/dashboard/profile';
  }

  isSettingsRoute(): boolean {
    return this.router.url.startsWith('/dashboard/settings');
  }

  get showMobileBackdrop(): boolean {
    return !this.isBackofficeMode && this.isMobile && this.isMenuExpanded;
  }

  get primarySidebarClassMap(): Record<string, boolean> {
    return {
      'z-[2000]': this.isMobile,
      'z-[200]': !this.isMobile,
      'w-14 items-center space-y-2': !this.isMenuExpanded && !this.isMobile,
      'w-64': this.isMenuExpanded,
      'w-0 overflow-hidden': this.isMobile && !this.isMenuExpanded,
    };
  }

  get secondarySidebarClassMap(): Record<string, boolean> {
    return {
      'w-0 md:w-0':
        !this.isOpen || this.isPersonalProfileRoute() || !this.showSidebarMenu,
      'w-84':
        this.isOpen &&
        !this.isMobile &&
        !this.isPersonalProfileRoute() &&
        this.showSidebarMenu &&
        (this.sidebarContent === 'menu' ||
          !this.isSettingsRoute() ||
          this.isProfileSidebarExpanded),
      'w-16':
        this.isOpen &&
        !this.isMobile &&
        !this.isPersonalProfileRoute() &&
        this.showSidebarMenu &&
        (this.sidebarContent === 'profile' || this.isSettingsRoute()) &&
        !this.isProfileSidebarExpanded,
      'w-0':
        (this.isOpen && this.isMobile) ||
        this.isPersonalProfileRoute() ||
        !this.showSidebarMenu,
    };
  }

  get mainContentClassMap(): Record<string, boolean> {
    return {
      'ml-0':
        this.isMobile ||
        (!this.isBackofficeMode &&
          !this.isOpen &&
          !this.isPersonalProfileRoute()),
      'ml-14':
        !this.isMobile &&
        !this.isMenuExpanded &&
        (this.isPersonalProfileRoute() ||
          this.isBackofficeMode ||
          this.isSettingsRoute() ||
          (!this.isBackofficeMode &&
            this.isPrimaryNavigationSection(this.activeMenuSection))),
      'ml-64':
        !this.isMobile &&
        this.isMenuExpanded &&
        (this.isPersonalProfileRoute() ||
          this.isBackofficeMode ||
          this.isSettingsRoute() ||
          (!this.isBackofficeMode &&
            this.isPrimaryNavigationSection(this.activeMenuSection))),
      'ml-52':
        !this.isPersonalProfileRoute() &&
        !this.isBackofficeMode &&
        !this.isSettingsRoute() &&
        this.isOpen &&
        !this.isMobile &&
        this.isMenuExpanded &&
        !this.isPrimaryNavigationSection(this.activeMenuSection) &&
        !this.isWideProfileSection(this.activeMenuSection),
      'ml-[344px]':
        !this.isPersonalProfileRoute() &&
        !this.isBackofficeMode &&
        !this.isSettingsRoute() &&
        !this.isMenuExpanded &&
        !this.isMobile &&
        this.isWideProfileSection(this.activeMenuSection),
      'ml-[544px]':
        !this.isPersonalProfileRoute() &&
        !this.isBackofficeMode &&
        !this.isSettingsRoute() &&
        this.isMenuExpanded &&
        !this.isMobile &&
        this.isWideProfileSection(this.activeMenuSection),
    };
  }

  get contentContainerClassMap(): Record<string, boolean> {
    return {
      'md:overflow-y-auto': !(this.showTopNavbar && !this.showHome && !this.isBackofficeMode),
      'xl:pl-0':
        this.shouldApplyDesktopContentInset(),
      'xl:pl-8':
        this.shouldApplyConfigurationContentInset() &&
        this.isMenuExpanded &&
        !this.isProfileSidebarExpanded,
      'xl:pl-14':
        this.shouldApplyConfigurationContentInset() &&
        !this.isMenuExpanded &&
        !this.isProfileSidebarExpanded,
      'xl:pl-72':
        this.shouldApplyConfigurationContentInset() &&
        this.isProfileSidebarExpanded,
    };
  }

  get shouldShowTopNavbar(): boolean {
    return (
      !this.showHome &&
      this.showTopNavbar &&
      !(this.isBackofficeMode && this.isSettingsRoute()) &&
      !this.isPersonalProfileRoute()
    );
  }

  get showMobileSidebarToggle(): boolean {
    if (this.isSettingsRoute()) {
      return true;
    }

    return ![
      'home',
      'profile',
      'knowledge',
      'alerts',
      'clients',
      'agents',
      'connections',
      'help-center',
    ].includes(this.activeMenuSection ?? '');
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  toggleMenuExpanded(): void {
    this.collapsibleMenuStateService.toggleMenuExpanded();
  }

  onDashboardNavigationItemClick(action: DashboardSidebarAction): void {
    if (!this.canNavigateToDashboardAction(action)) {
      this.notifyBlockedDashboardAction(action);
      this.enforceAccessRoute(true);
      return;
    }

    switch (action) {
      case 'home':
        this.openHome();
        break;
      case 'new-chat':
        this.newChat();
        break;
      case 'agents':
        this.openAgents();
        break;
      case 'knowledge':
        this.openKnowledge();
        break;
      case 'alerts':
        this.openAlerts();
        break;
      case 'clients':
        this.openClients();
        break;
      case 'help-center':
        this.openHelpCenter();
        break;
      case 'settings':
        this.openSettings();
        break;
      case 'connections':
        this.openConnections();
        break;
    }
  }

  openHome(): void {
    this.setActiveMenuSection('home');
    sessionStorage.removeItem('login_destination');
    this.navigateAndCloseMobile(['/dashboard/home']);

    if (!this.isMobile && !this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(true);
    }
  }

  newChat(): void {
    const defaultAgent = this.agentStateService.findDefaultAgent();

    if (defaultAgent) {
      const idToUse =
        defaultAgent.agent_id ??
        defaultAgent.agent_config?.id ??
        defaultAgent.id;
      const agentType =
        defaultAgent.category === 'datasources' ? 'datasource' : 'agent';

      localStorage.setItem('type_agent', agentType);
      localStorage.setItem('agent_id', idToUse.toString());

      this.agentStateService.setAgentId(idToUse);
      this.agentStateService.setCurrentAgent(defaultAgent.agent_config.name);

      this.chatStateService.startNewChat(
        this.agentStateService,
        this.navbarViewManagerService,
      );

      this.showHome = false;
      this.showSidebarMenu = true;
      this.showTopNavbar = true;
      this.activeMenuSection = null;
      this.navigateAndCloseMobile(['/dashboard/chat']);
    } else {
      this.setActiveMenuSection('agents');
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = this.isMobile;
      this.navigateAndCloseMobile(['/dashboard/agents']);
    }
  }

  openAgents(): void {
    this.setActiveMenuSection('agents');
    this.navigateAndCloseMobile(['/dashboard/agents']);
  }

  openConnections(): void {
    this.setActiveMenuSection('connections');
    this.showTopNavbar = false;
    this.navigateAndCloseMobile(['/dashboard/connections']);
  }

  openHelpCenter(): void {
    this.setActiveMenuSection('help-center');
    this.showTopNavbar = false;
    this.navigateAndCloseMobile(['/dashboard/help-center']);
  }

  openKnowledge(): void {
    this.setActiveMenuSection('knowledge');
    this.navigateAndCloseMobile(['/dashboard/knowledge']);
  }

  openAlerts(): void {
    this.setActiveMenuSection('alerts');
    this.navigateAndCloseMobile(['/dashboard/alerts']);
  }

  openClients(): void {
    this.setActiveMenuSection('clients');
    this.navigateAndCloseMobile(['/dashboard/clients']);
  }

  openSettings(): void {
    this.showSidebarMenu = false;
    this.showTopNavbar = false;
    this.navigateAndCloseMobile(['/dashboard/settings', 'general']);
  }

  private applyNavigationShellState(state: NavigationShellState): void {
    const wasMobile = this.isMobile;
    const wasSidebarOpen = this.isOpen;

    this.isOpen = state.isSidebarOpen;
    this.isMobile = state.isMobile;
    this.isMenuExpanded = state.isMenuExpanded;
    this.isProfileSidebarExpanded = state.isProfileSidebarExpanded;
    this.isTablet =
      state.screenSize === ScreenSize.MD || state.screenSize === ScreenSize.LG;

    if (wasMobile !== this.isMobile) {
      this.updateActiveMenuFromRoute();

      if (this.isMobile && this.isMenuExpanded) {
        this.collapsibleMenuStateService.setMenuExpanded(false);
      }

      if (this.isMobile && wasSidebarOpen) {
        this.toggleSidebar();
      } else if (!this.isMobile && !state.isSidebarOpen) {
        this.toggleSidebar();
      }
    } else {
      this.applySettingsSidebarBehavior();
    }
  }

  private updateActiveMenuFromRoute(): void {
    const url = this.router.url;

    if (this.isBackofficeMode) {
      if (url.includes('/home-backoffice')) {
        this.showHome = true;
        this.showSidebarMenu = false;
        this.showTopNavbar = true;
      } else if (url.includes('/profile') && url.includes('mode=backoffice')) {
        this.showHome = false;
        this.showSidebarMenu = false;
        this.showTopNavbar = false;
      } else if (url.includes('/settings')) {
        this.activeMenuSection = null;
        this.showHome = false;
        this.showSidebarMenu = false;
        this.showTopNavbar = false;
      }
      return;
    }

    if (url.includes('/home') && !url.includes('/home-backoffice')) {
      this.activeMenuSection = 'home';
      this.showHome = true;
      this.showSidebarMenu = false;
      this.showTopNavbar = true;
      this.sidebarProfileStateService.showMenu();

      if (!this.isMobile && !this.isMenuExpanded) {
        this.collapsibleMenuStateService.setMenuExpanded(true);
      }
    } else if (url.includes('/training')) {
      this.activeMenuSection = 'training';
      this.showHome = true;
      this.showSidebarMenu = false;
      this.showTopNavbar = true;
      this.sidebarProfileStateService.showMenu();
    } else if (
      (url.includes('/agents') || url.includes('/agent-config')) &&
      !url.includes('/settings')
    ) {
      this.activeMenuSection = 'agents';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = this.isMobile;
      this.sidebarProfileStateService.showMenu();
    } else if (url.includes('/connections') && !url.includes('/settings')) {
      this.activeMenuSection = 'connections';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showMenu();
    } else if (url.includes('/help-center')) {
      this.activeMenuSection = 'help-center';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showMenu();
    } else if (url.includes('/knowledge')) {
      this.activeMenuSection = 'knowledge';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
    } else if (url.includes('/alerts') && !url.includes('/settings')) {
      this.activeMenuSection = 'alerts';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
    } else if (url.includes('/clients')) {
      this.activeMenuSection = 'clients';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
    } else if (url.includes('/chat')) {
      this.activeMenuSection = null;
      this.showHome = false;
      this.showSidebarMenu = true;
      this.showTopNavbar = true;
      this.sidebarProfileStateService.showMenu();
    } else if (url.includes('/profile') && !url.includes('mode=backoffice')) {
      this.activeMenuSection = this.profileService.isCompanyProfileActive()
        ? 'company'
        : 'profile';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showProfile();
    } else if (url.includes('/company')) {
      this.activeMenuSection = 'company';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showProfile();
    } else if (url.includes('/user-management')) {
      this.activeMenuSection = 'users';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showProfile();
    } else if (url.includes('/payments-invoices')) {
      this.activeMenuSection = 'payments';
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showProfile();
    } else if (url.includes('/settings')) {
      this.activeMenuSection = null;
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = false;
      this.sidebarProfileStateService.showProfile();
      this.navigationShellStateService.ensureDesktopSidebarOpen();
    } else {
      this.activeMenuSection = null;
      this.showHome = false;
      this.showSidebarMenu = false;
      this.showTopNavbar = true;
    }

    this.applySettingsSidebarBehavior();
    this.enforceAccessRoute();
  }

  private applySettingsSidebarBehavior(): void {
    if (this.isSettingsRoute() && this.isTablet && !this.isMobile) {
      if (this.menuExpandedBeforeSettings === null) {
        this.menuExpandedBeforeSettings = this.isMenuExpanded;
      }
      if (this.isMenuExpanded) {
        this.collapsibleMenuStateService.setMenuExpanded(false);
      }

      if (this.profileSidebarExpandedBeforeSettings === null) {
        this.profileSidebarExpandedBeforeSettings =
          this.isProfileSidebarExpanded;
      }
      if (this.isProfileSidebarExpanded) {
        this.collapsibleMenuStateService.setProfileSidebarExpanded(false);
      }
      return;
    }

    if (this.menuExpandedBeforeSettings !== null) {
      this.collapsibleMenuStateService.setMenuExpanded(
        this.menuExpandedBeforeSettings,
      );
      this.menuExpandedBeforeSettings = null;
    }

    if (this.profileSidebarExpandedBeforeSettings !== null) {
      this.collapsibleMenuStateService.setProfileSidebarExpanded(
        this.profileSidebarExpandedBeforeSettings,
      );
      this.profileSidebarExpandedBeforeSettings = null;
    }
  }

  private loadActiveMenuSection(): void {
    const savedSection = localStorage.getItem(
      this.ACTIVE_MENU_KEY,
    ) as ActiveMenuSection;
    this.activeMenuSection = savedSection;
  }

  private setActiveMenuSection(section: ActiveMenuSection): void {
    this.activeMenuSection = section;
    if (section) {
      localStorage.setItem(this.ACTIVE_MENU_KEY, section);
    } else {
      localStorage.removeItem(this.ACTIVE_MENU_KEY);
    }
  }

  private closeMobileChromeIfNeeded(): void {
    if (this.isMobile && this.isMenuExpanded) {
      this.collapsibleMenuStateService.setMenuExpanded(false);
    }
    if (this.isMobile && this.isOpen) {
      this.sidebarService.toggleSidebar();
    }
  }

  private navigateAndCloseMobile(commands: string[]): void {
    this.router.navigate(commands);
    this.closeMobileChromeIfNeeded();
  }

  private isPrimaryNavigationSection(section: ActiveMenuSection): boolean {
    return [
      'home',
      'agents',
      'connections',
      'training',
      'help-center',
      'knowledge',
      'alerts',
      'clients',
    ].includes(section ?? '');
  }

  private isWideProfileSection(section: ActiveMenuSection): boolean {
    return ['company', 'users', 'payments'].includes(section ?? '');
  }

  private shouldApplyDesktopContentInset(): boolean {
    return !this.isMobile && !this.isConfigurationSection() && !this.showHome;
  }

  private shouldApplyConfigurationContentInset(): boolean {
    return !this.isMobile && this.isOpen && this.isConfigurationSection();
  }

  private isConfigurationSection(): boolean {
    return (
      this.isSettingsRoute() ||
      ['profile', 'company', 'users', 'payments'].includes(
        this.activeMenuSection ?? '',
      )
    );
  }

  private canNavigateToDashboardAction(action: DashboardSidebarAction): boolean {
    if (this.isLoggingOut) {
      return false;
    }

    return true;
  }

  private notifyBlockedDashboardAction(action: DashboardSidebarAction): void {
    const label = this.getDashboardActionLabel(action);

    this.showAccessRestrictionAlert(
      'Navegación no disponible',
      `No se pudo abrir ${label} en este momento.`,
    );
  }

  private showAccessRestrictionAlert(title: string, message: string): void {
    if (this.accessAlertTimer) {
      clearTimeout(this.accessAlertTimer);
      this.accessAlertTimer = null;
    }

    this.accessAlertTitle = title;
    this.accessAlertMessage = message;
    this.accessAlertType = 'warning';
    this.accessAlertToken += 1;
    this.showAccessAlert = true;

    this.accessAlertTimer = setTimeout(() => {
      this.showAccessAlert = false;
      this.accessAlertTimer = null;
    }, 4200);
  }

  private getDashboardActionLabel(action: DashboardSidebarAction): string {
    const labels: Record<DashboardSidebarAction, string> = {
      home: 'Inicio',
      'new-chat': 'Nuevo chat',
      agents: 'Agentes',
      knowledge: 'Contexto de empresa',
      alerts: 'Alertas',
      clients: 'Clientes',
      'help-center': 'Centro de ayuda',
      settings: 'Configuración',
      connections: 'Conexiones',
    };
    return labels[action] || 'este módulo';
  }

  private enforceAccessRoute(fromNavigationAction = false): void {
    return;
  }

  get shouldShowTrialNotice(): boolean {
    return (
      !!this.trialInfo?.started &&
      !this.hasActiveSubscription &&
      !this.hasActiveSubscriptionFromSession
    );
  }

  get trialNoticeMessage(): string {
    if (!this.trialInfo?.started) {
      return '';
    }

    if (this.trialInfo.expired) {
      return 'Tu prueba gratuita ha terminado. Activa un plan para seguir usando el chat.';
    }

    const days = Math.max(0, Number(this.trialInfo.days_remaining) || 0);
    return `Te quedan ${days} día${days === 1 ? '' : 's'} de prueba`;
  }

  get trialNoticeClass(): string {
    return this.trialInfo?.expired
      ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100'
      : 'border-[var(--color-brand-300)] bg-white text-[var(--color-brand-500)] dark:border-[var(--color-brand-500)] dark:bg-[var(--color-dark-bg-secondary)] dark:text-[var(--color-brand-300)]';
  }
}
